import { Fragment, useEffect, useState } from 'react'
import { Dialog, Menu, Transition, Popover, Disclosure, Combobox } from '@headlessui/react'
import {
  HomeIcon,
  ChipIcon,
  FolderIcon,
  TerminalIcon,
  MenuAlt2Icon,
  XIcon,
  GlobeIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  ClipboardListIcon,
  // LightBulbIcon,
  // ExclamationCircleIcon,
  // ShieldCheckIcon,
  EyeIcon,
  TemplateIcon,
} from '@heroicons/react/outline'
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useSnackbar } from 'notistack';
import * as organizationAPI from 'apis/organization';
import { HelpCentre } from './components/HelpCentre';
import moment from 'moment-timezone';
import { can } from 'helpers';
import OpenAIDialog from 'components/common/OpenAIDialog';
import { AIDialog } from './components/AIDialog';
import { QADialog } from './components/QADialog';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}
const notifTheme = {"icon":{"borderColor":"#6B7280","width":"24px"},"unseenBadge":{"backgroundColor":"#DF4759"},"header":{"backgroundColor":"#004469","textColor":"#ffffff","borderRadius":"16px"},"footer":{"backgroundColor":"#004469","textColor":"#ffffff","borderRadius":"16px"},"notification":{"default":{"textColor":"#15091F","borderRadius":"8px","backgroundColor":"#004469"},"unseen":{"backgroundColor":"#004469","textColor":"#15091F","borderRadius":"8px"},"unread":{"backgroundColor":"#004469","textColor":"#15091F","borderRadius":"8px"}}};
const notificationAPIKey = process.env.REACT_APP_MAGICBELL_API_KEY;

export default function Main({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const navigation = [
    {
      name: 'Dashboard',
      icon: HomeIcon,
      href: '/',
      resource: '*',
    },
    {
      name: 'Integrations',
      icon: ChipIcon,
      href: '/integrations',
      resource: '*',
    },
    {
      name: 'Inventory',
      icon: FolderIcon,
      href: '/inventory',
      resource: '*',
    },
    {
      name: 'Digital Workers',
      icon: TerminalIcon,
      href: '/digitalworkers',
      resource: '*'
    },
    {
      name: 'Observability',
      icon: EyeIcon,
      href: '/observability',
      resource: '*',
      allowedRoles: ['admin']
    },
    {
      name: 'Templates',
      icon: TemplateIcon,
      href: '/templates',
      resource: '*',
    }
  ];

  const [aiDialogOpen, setAIDialogOpen] = useState(false);
  const [qaDialogOpen, setQADialogOpen] = useState(false);
  
  const userNavigation = [
    // { name: 'Your Profile', href: '#' },
    { name: 'Sign out', href: '', onClick: () => {
      Auth.signOut().then(() => {
        signOut();
        window.location.href = '/signin';
      }).catch((e) => {
        console.log(e);
      });
    } },
  ];

  const toggleAIDialog = (e) => {
    if (e.keyCode === 191 && e.ctrlKey) {
      setAIDialogOpen(true);
    }
  }

  useEffect(() => {
    if (qaDialogOpen === false) {
      addEventListener('keydown', toggleAIDialog);
    } else {
      removeEventListener('keydown', toggleAIDialog);
    }

    return () => {
      removeEventListener('keydown', toggleAIDialog);
    }
  }, [qaDialogOpen]);

  const isCurrent = (href) => (history.location.pathname === href || (history.location.pathname.startsWith(href) && href !== '/'))
  const platform = useStoreState((state) => state.platform);
  const signOut = useStoreActions((actions) => actions.platform.signOut);
  const assignRole = useStoreActions((actions) => actions.platform.assignRole);
  const assignPermissions = useStoreActions((actions) => actions.platform.assignPermissions);
  const setSubscription = useStoreActions((actions) => actions.platform.setSubscription);
  const setName = useStoreActions((actions) => actions.platform.setName);
  const setNotificationKey = useStoreActions((actions) => actions.platform.setNotificationKey);
  const setTours = useStoreActions((actions) => actions.platform.setTours);
  const [helpCentreOpen, setHelpCentreOpen] = useState(false);
  const [newUser, setNewUser] = useState(false);

  const getInitials = () => {
    let initials = '';
    if (platform.firstName && platform.firstName !== '') {
      initials += platform.firstName[0].toUpperCase();
    }
    if (platform.lastName && platform.lastName !== '') {
      initials += platform.lastName[0].toUpperCase();
    }
    return initials;
  };

  const currentOrgAndTenant = () => {
    const org = platform.orgs.find((org) => org.orgId === platform.orgId);
    if (org) {
      const tenant = org.childTenants.find((tenant) => tenant.childTenantId === platform.tenantId);
      if (tenant) {
        return {
          org,
          tenant,
        };
      } else {
        enqueueSnackbar('Invalid tenant.', { variant: 'error' });
        signOut();
      }
    } else {
      enqueueSnackbar('Invalid organization.', { variant: 'error' });
      signOut();
    }
  }

  useEffect(() => {
    organizationAPI.user(platform.orgId, platform.userId)
      .then(({ status, data }) => {
        if (status === 200) {
          setNewUser(data.newUser);
          setName({ firstName: data.firstName, lastName: data.lastName });
          assignRole(data.role);
          if (data.tours != null) {
            setTours(data.tours);
          }
          setNotificationKey(data.notifKey);
          // check if the user has identityId
          if (!data.identityId) {
            Auth.currentCredentials().then((credentials) => {
              organizationAPI.update(platform.orgId, platform.userId, {
                identityId: credentials.identityId,
              });
            });
          }
        }
      });

    organizationAPI.get(platform.orgId)
      .then(({ status, data }) => {
        if (status === 200) {
          setSubscription(data);
        }
      });

    organizationAPI.getPermissions(platform.orgId)
      .then(({ status, data }) => {
        if (status === 200) {
          assignPermissions(data);
        }
      })
    
  }, []);

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <HelpCentre open={helpCentreOpen} setOpen={setHelpCentreOpen} />
      {/* Narrow sidebar */}
      <div style={{ backgroundColor: '#004469' }} className="hidden overflow-y-auto md:block">
        <div className="w-full py-6 flex flex-col items-center">
          <div
            className="flex-shrink-0 flex items-center cursor-pointer"
            onClick={() => history.push('/')}
          >
            <img
              className="h-8 w-auto"
              src={require('assets/images/logo_transparent_white.svg').default}
              alt="Xpertly"
            />
          </div>
          <div className="flex-1 mt-6 w-full px-2 space-y-1">
            {navigation.filter((nav) => can(platform.role, 'view', nav.resource, platform.permissions, nav.allowedRoles)).map((item) => (
              !item.children ? (
                <a
                  key={item.name}
                  onClick={() => history.push(item.href)}
                  className={classNames(
                    isCurrent(item.href) ? 'bg-sky-800 text-white' : 'text-sky-100 hover:bg-sky-800 hover:text-white',
                    'group w-full p-3 rounded-md flex items-center text-sm font-medium cursor-pointer'
                  )}
                  aria-current={isCurrent(item.href) ? 'page' : undefined}
                >
                  { item.icon && (
                    <item.icon
                      className="mr-3 h-6 w-6 flex-shrink-0"
                      aria-hidden="true"
                    />
                  ) }
                  <span>{item.name}</span>
                </a>
              ) : (
                <Disclosure as="div"
                  key={item.name}
                  className="space-y-1" defaultOpen={isCurrent(item.href)}
                  onClick={() => history.push(item.href)}
                >
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        className={classNames(
                          isCurrent(item.href) && open
                            ? 'bg-sky-800 text-white'
                            : 'text-sky-100 hover:bg-sky-800 hover:text-white',
                          'group w-full flex items-center p-3 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500 hover:bg-sky-800 hover:text-white space-x-3'
                        )}
                      >
                        { item.icon && (
                          <item.icon
                            className="h-6 w-6 flex-shrink-0"
                            aria-hidden="true"
                          />
                        ) }
                        <span className="flex-1">{item.name}</span>
                        <svg
                          className={classNames(
                            isCurrent(item.href) && open ? 'text-white rotate-90' : 'text-sky-300 group-hover:text-white',
                            'h-6 w-6 transform'
                          )}
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                        </svg>
                      </Disclosure.Button>
                      <Disclosure.Panel className="space-y-1">
                        {item.children.map((subItem) => (
                          <Disclosure.Button
                            key={subItem.name}
                            as="button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (history.location.href !== subItem.href) history.push(subItem.href);
                            }}
                            className={classNames(
                              isCurrent(subItem.href) && open ?
                              'bg-sky-800 text-white' : 'text-sky-100 hover:bg-sky-800 hover:text-white',
                              "group flex w-full items-center rounded-md p-3 text-sm font-medium text-white hover:bg-sky-800 hover:text-white"
                            )}
                          >
                            { subItem.icon && (
                              <subItem.icon
                                className="h-6 w-6 flex-shrink-0 mr-3"
                                aria-hidden="true"
                              />
                            ) }
                            {subItem.name}
                          </Disclosure.Button>
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              )
            ))}
          </div>
        </div>
      </div>

      {/* New User Dialog */}
      <Transition appear show={newUser} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={() => null}
        >
          <div className="min-h-screen px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  Hi, { platform.firstName } { platform.lastName }
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    It looks like it's your first time here, find your way around with our guided tour.
                  </p>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    onClick={() => {
                      organizationAPI.update(platform.orgId, platform.userId, { newUser: false });
                      setNewUser(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
                    onClick={() => {
                      organizationAPI.update(platform.orgId, platform.userId, { newUser: false })
                        .then(({ status }) => {
                          if (status === 200) {
                            history.push('/digitalworkers/create?category=Digital Workers&tour=1');
                          }
                        })
                    }}
                  >
                    Take me there!
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Mobile menu */}
      <Transition.Root show={mobileMenuOpen} as={Fragment}>
        <Dialog as="div" className="md:hidden" onClose={setMobileMenuOpen}>
          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative max-w-xs w-full bg-sky-700 pt-5 pb-4 flex-1 flex flex-col">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-1 right-0 -mr-14 p-1">
                    <button
                      type="button"
                      className="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                      <span className="sr-only">Close sidebar</span>
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-shrink-0 px-4 flex items-center">
                  <img
                    className="h-8 w-auto"
                    src={require('assets/images/logo_transparent.svg').default}
                    alt="Xpertly"
                  />
                </div>
                <div className="mt-5 flex-1 h-0 px-2 overflow-y-auto">
                  <nav className="h-full flex flex-col">
                    <div className="space-y-1">
                      {navigation.map((item) => (
                        <a
                          key={item.name}
                          href={item.href}
                          className={classNames(
                            isCurrent(item.href)
                              ? 'bg-sky-800 text-white'
                              : 'text-sky-100 hover:bg-sky-800 hover:text-white',
                            'group py-2 px-3 rounded-md flex items-center text-sm font-medium'
                          )}
                          aria-current={isCurrent(item.href) ? 'page' : undefined}
                        >
                          {/* <item.icon
                            className={classNames(
                              isCurrent(item.href) ? 'text-white' : 'text-sky-300 group-hover:text-white',
                              'h-6 w-6'
                            )}
                            aria-hidden="true"
                          /> */}
                          <span>{item.name}</span>
                        </a>
                      ))}
                    </div>
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="w-full">
          <div className="relative z-10 flex-shrink-0 h-16 bg-white border-b border-gray-200 shadow-sm flex">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 md:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex-1 flex justify-between px-5">
              <div className="flex items-center space-x-4">
                {/* Orgs & Tenants */}
                <Menu as="div" className="relative flex-shrink-0">
                  <div className="flex flex-row items-center">
                    <Menu.Button className="p-1 bg-white rounded-full flex text-sm focus:outline-none items-center">
                      <GlobeIcon className="h-6 w-6" aria-hidden="true" />
                      <span className="sr-only">Switch Org and Tenant</span>
                      <div className="flex flex-col text-left ml-1">
                        <div className="text-xs">{ currentOrgAndTenant()?.org?.orgName }</div>
                        <div className="text-sm">{ currentOrgAndTenant()?.tenant?.childTenantName }</div>
                      </div>
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-left absolute left-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-40">
                      {platform.orgs.map((org) => (
                        <div key={org.orgId}>
                          <div>
                            <a
                              className={classNames(
                                (platform.orgId === org.orgId) ? 'bg-teal-50' : '',
                                'block px-4 py-2 text-sm cursor-not-allowed'
                              )}
                            >
                              {org.orgName}
                            </a>
                          </div>
                          { org.childTenants && org.childTenants.map((tenant) => (
                            <Menu.Item key={tenant.childTenantId}>
                              {({ active }) => (
                                <a
                                  onClick={(e) => {
                                    localStorage.setItem('orgId', org.orgId);
                                    localStorage.setItem('tenantId', tenant.childTenantId);
                                    window.location.href = '/';
                                  }}
                                  className={classNames(
                                    (active || platform.tenantId === tenant.childTenantId) ? 'bg-gray-100' : '',
                                    'block px-6 py-2 text-sm text-gray-700'
                                  )}
                                >
                                  {tenant.childTenantName}
                                </a>
                              )}
                            </Menu.Item>
                          )) }
                        </div>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
              {/* <div className="flex items-center w-96">
                <div className="relative flex items-center w-full">
                  <input
                    type="text"
                    name="search"
                    id="search"
                    className="block w-full rounded-md border-0 py-1.5 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 cursor-pointer"
                    placeholder={`Get started with Xpertly Assistant`}
                    onClick={() => setAIDialogOpen(true)}
                  />
                  <div className="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
                    <kbd className="inline-flex items-center rounded border border-gray-200 px-2 font-sans text-xs text-gray-400">
                      Ctrl + /
                    </kbd>
                  </div>
                </div>
                <AIDialog open={aiDialogOpen} setOpen={setAIDialogOpen} setQADialogOpen={setQADialogOpen} />
                <QADialog open={qaDialogOpen} setOpen={setQADialogOpen} />
              </div> */}
              <div className="flex items-center space-x-4">
                {/* Profile dropdown */}
                <Menu as="div" className="relative flex-shrink-0">
                  <div>
                    <Menu.Button className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500">
                        <span className="text-sm font-medium leading-none text-white">{getInitials()}</span>
                      </span>
                      <span className="sr-only">Open user menu</span>
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <a
                              onClick={item.onClick}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block px-4 py-2 text-sm text-gray-700'
                              )}
                            >
                              {item.name}
                            </a>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
                {/* {platform.notificationKey && 
                <MagicBell
                  apiKey={notificationAPIKey}
                  userExternalId={platform.userId}
                  userKey={platform.notificationKey}
                  theme={notifTheme}
                  onNewNotification={(notification) => {
                    if (notification.category == 'Urgent') {
                      const action = key => (
                          <Fragment>
                              <button
                                className='bg-white hover:bg-blue-500 text-blue-700 font-semibold hover:text-white py-2 px-4 border border-blue-500 hover:border-transparent rounded'
                                onClick={() => { closeSnackbar(key) }}>
                                  Dismiss
                              </button>
                          </Fragment>
                      );
                      enqueueSnackbar(`${notification.content}`, { variant: 'info', persist: true, action });
                    }
                  }}
                >
                  {(props) => <FloatingNotificationInbox width={400} height={500} {...props} />}
                </MagicBell>} */}
                {/* Help Centre */}
                <button
                  className="p-1 bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  onClick={() => setHelpCentreOpen(true)}
                >
                  <QuestionMarkCircleIcon className="h-6 w-6" />
                  <span className="sr-only">Help Centre</span>
                </button>
                {/* Settings */}
                <button
                  className="p-1 bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  type="button"
                  onClick={() => history.push('/settings/profile')}
                >
                  <CogIcon className="h-6 w-6" aria-hidden="true" />
                  <span className="sr-only">Settings</span>
                </button>
              </div>
            </div>
          </div>
          { platform.subscription?.xpertlySubscriptionType === 'Team Trial' && (
            <div className="relative bg-yellow-300">
              <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8">
                <div className="pr-16 sm:text-center sm:px-16">
                  <p className="font-medium">
                    {/* <span className="md:hidden">We announced a new product!</span> */}
                    <span className="hidden md:inline">
                      { moment.utc(platform.subscription?.xpertlyTrialEndDate).diff(moment.now(), 'seconds') > 0 ? (
                        `Your trial ends in ${moment.utc(platform.subscription?.xpertlyTrialEndDate).diff(moment.now(), 'days')} days.`
                      ) : `Your Trial has ended.` }
                    </span>

                    <span className="block sm:ml-2 sm:inline-block">
                      <a onClick={() => history.push('/settings/billing')} className="font-bold underline cursor-pointer">
                        {' '}
                        Subscribe <span aria-hidden="true">&rarr;</span>
                      </a>
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ) }
        </header>

        {/* Main content */}
        <div className="flex-1 flex items-stretch overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            {/* Primary column */}
            <section
              aria-labelledby="primary-heading"
              className={
                classNames(
                  (history.location.pathname === '/digitalworkers/create' || (history.location.pathname.startsWith('/digitalworkers') && history.location.pathname.endsWith('/edit'))) ? '' : 'p-5',
                  "min-w-0 flex-1 h-full flex flex-col overflow-scroll lg:order-last"
                )}
            >
              {children}
            </section>
          </main>
        </div>

        {/* Other Utils */}
        <OpenAIDialog />
      </div>
    </div>
  )
}